import React, { memo, useCallback, useMemo, useState } from "react";
import { Pressable, View } from "react-native";
import { addHours, addMinutes, addYears, format, isToday, isTomorrow } from "date-fns";
import {
  Text,
  Banner,
  Button,
  RadioButton,
  SegmentedControl,
  IncrementableInput,
  Icon,
} from "@visioni/a3s-ui/components";
import type { AlarmOverride } from "../../schema";
import { dateTimeRecordToLocalisedDateTime, makeDateTimeRecord, type DateTimeRecord } from "@visioni/schedules";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import Animated, { LinearTransition } from "react-native-reanimated";

export type AlarmException = {
  id: string;
  reason: string;
  action: "armed" | "disarmed";
  period: {
    start: DateTimeRecord;
    end: DateTimeRecord;
  };
};

type TimeUnit = "hours" | "minutes";
type SelectionMode = "duration" | "specificTime" | "indefinitely";

const TIME_UNITS: TimeUnit[] = ["hours", "minutes"];
const TIME_UNIT_OPTIONS = TIME_UNITS.map((unit) => ({ title: unit }));

interface TimePickerRenderProps {
  readonly isVisible: boolean;
  readonly hour: number;
  readonly minute: number;
  readonly onConfirm: (hour: number, minute: number) => void;
  readonly onCancel: () => void;
}

export interface ArmDisarmPanelContentProps {
  readonly mode: "override" | "adjust";
  readonly targetAction: "armed" | "disarmed";
  readonly activeOverride?: AlarmOverride | null;
  readonly timezone?: string;
  readonly onConfirm?: (exception: AlarmException) => void;
  readonly selectorWidth?: number;
  readonly renderTimePicker?: (props: TimePickerRenderProps) => React.ReactNode;
  /** Controls whether copy refers to a single camera or a site. Defaults to "camera". */
  readonly scope?: "camera" | "site";
  /** When true, the confirm button shows a loading state and is disabled. */
  readonly isSubmitting?: boolean;
}

export const ArmDisarmPanelContent = memo(function ArmDisarmPanelContent({
  mode,
  targetAction,
  activeOverride,
  timezone = "UTC",
  onConfirm,
  selectorWidth,
  renderTimePicker,
  scope = "camera",
  isSubmitting = false,
}: ArmDisarmPanelContentProps) {
  const { theme } = useUnistyles();
  const [selectionMode, setSelectionMode] = useState<SelectionMode>("duration");
  const [timeUnit, setTimeUnit] = useState<TimeUnit>("hours");
  const [timeValue, setTimeValue] = useState<number>(1);
  const [specificHour, setSpecificHour] = useState(9);
  const [specificMinute, setSpecificMinute] = useState(0);
  const [showTimePicker, setShowTimePicker] = useState(false);

  const isAdjustMode = mode === "adjust";
  const isTargetArmed = targetAction === "armed";
  const subject = scope === "site" ? "all cameras" : "your camera";
  const subjectShort = scope === "site" ? "All cameras" : "Camera";

  const armDisarmedTitle = `${isTargetArmed ? "Arm" : "Disarm"} temporarily`;
  const adjustTitle = `Keep ${isTargetArmed ? "armed" : "disarmed"} for longer`;
  const armDisarmedSubtitle = `How long would you like to ${isTargetArmed ? "arm" : "disarm"} ${subject} for?`;
  const adjustSubtitle = `How much longer would you like to stay ${isTargetArmed ? "armed" : "disarmed"}?`;

  const getEndDateRecord = useCallback(() => {
    const now = new Date();
    let end: Date;

    if (selectionMode === "duration") {
      end = timeUnit === "hours" ? addHours(now, timeValue) : addMinutes(now, timeValue);
    } else if (selectionMode === "specificTime") {
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), specificHour, specificMinute);
      if (end <= now) {
        end.setDate(end.getDate() + 1);
      }
    } else {
      end = addYears(now, 10);
    }

    return makeDateTimeRecord(end.getFullYear(), end.getMonth() + 1, end.getDate(), end.getHours(), end.getMinutes());
  }, [selectionMode, specificHour, specificMinute, timeUnit, timeValue]);

  const formatEndDateTime = useCallback((endDate: ReturnType<typeof getEndDateRecord>) => {
    const endDateTime = new Date(endDate.year, endDate.month - 1, endDate.day, endDate.hour, endDate.minute);
    const timeStr = format(endDateTime, "h:mm a");
    const dateStr =
      isToday(endDateTime) ? "today"
      : isTomorrow(endDateTime) ? "tomorrow"
      : format(endDateTime, "MMM d");
    return { timeStr, dateStr };
  }, []);

  const adjustOverrideMessage = useMemo(() => {
    if (!isAdjustMode || !activeOverride) return "";
    const actionLabel = activeOverride.action === "armed" ? "armed" : "disarmed";

    if (selectionMode === "indefinitely") {
      return `${subjectShort} will remain ${actionLabel} indefinitely.`;
    }

    let newEndDate: Date;
    if (selectionMode === "specificTime") {
      const now = new Date();
      newEndDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), specificHour, specificMinute);
      if (newEndDate <= now) {
        newEndDate.setDate(newEndDate.getDate() + 1);
      }
    } else {
      const base =
        activeOverride.period.end ?
          dateTimeRecordToLocalisedDateTime(activeOverride.period.end, timezone).toJSDate()
        : new Date();
      const minutes = timeUnit === "hours" ? timeValue * 60 : timeValue;
      newEndDate = new Date(base.getTime() + minutes * 60 * 1000);
    }

    const endRecord = makeDateTimeRecord(
      newEndDate.getFullYear(),
      newEndDate.getMonth() + 1,
      newEndDate.getDate(),
      newEndDate.getHours(),
      newEndDate.getMinutes(),
    );
    const { timeStr, dateStr } = formatEndDateTime(endRecord);
    return `${subjectShort} will remain ${actionLabel} until ${timeStr} ${dateStr}.`;
  }, [
    activeOverride,
    formatEndDateTime,
    isAdjustMode,
    selectionMode,
    specificHour,
    specificMinute,
    subjectShort,
    timeUnit,
    timeValue,
    timezone,
  ]);

  const timeMax = timeUnit === "hours" ? 24 : 60;

  const bannerMessage = useMemo(() => {
    if (isAdjustMode && activeOverride) {
      return adjustOverrideMessage;
    }
    if (selectionMode === "indefinitely") {
      return `${subjectShort} will remain ${isTargetArmed ? "armed" : "disarmed"} indefinitely.`;
    }
    if (selectionMode === "duration" && (timeValue < 1 || timeValue > timeMax)) {
      return null;
    }
    const endDate = getEndDateRecord();
    const { timeStr, dateStr } = formatEndDateTime(endDate);
    return `${subjectShort} will remain ${isTargetArmed ? "armed" : "disarmed"} until ${timeStr} ${dateStr}.`;
  }, [
    activeOverride,
    adjustOverrideMessage,
    formatEndDateTime,
    getEndDateRecord,
    isAdjustMode,
    isTargetArmed,
    selectionMode,
    subjectShort,
    timeMax,
    timeValue,
  ]);

  const handleConfirm = useCallback(() => {
    if (selectionMode === "duration" && (timeValue < 1 || timeValue > timeMax)) return;

    const now = new Date();
    let startDate: DateTimeRecord;

    if (isAdjustMode && selectionMode === "specificTime" && activeOverride?.period.end) {
      startDate = activeOverride.period.end;
    } else {
      startDate = makeDateTimeRecord(
        now.getFullYear(),
        now.getMonth() + 1,
        now.getDate(),
        now.getHours(),
        now.getMinutes(),
      );
    }

    const endDate = getEndDateRecord();
    const exception: AlarmException = {
      id: `temp-${String(Date.now())}`,
      reason:
        isAdjustMode ?
          `Extended ${isTargetArmed ? "arm" : "disarm"} period`
        : `Temporary ${isTargetArmed ? "arm" : "disarm"}`,
      action: targetAction,
      period: {
        start: startDate,
        end: endDate,
      },
    };
    onConfirm?.(exception);
  }, [
    activeOverride,
    getEndDateRecord,
    isAdjustMode,
    isTargetArmed,
    onConfirm,
    selectionMode,
    targetAction,
    timeMax,
    timeValue,
  ]);

  const handleTimeUnitPress = useCallback(
    (option: string) => {
      const unit = option as TimeUnit;
      if (timeValue > 24 && unit === "hours") {
        setTimeValue(24);
      }
      setTimeUnit(unit);
    },
    [timeValue],
  );

  const hasUpperLimitError = selectionMode === "duration" && timeValue > timeMax;
  const hasLowerLimitError = selectionMode === "duration" && timeValue < 1;
  const hasTimeError = hasUpperLimitError || hasLowerLimitError;
  const timeHelperText =
    hasUpperLimitError ? `Max ${String(timeMax)} ${timeUnit}`
    : hasLowerLimitError ? `Min 1 ${timeUnit.slice(0, -1)}`
    : undefined;

  const formattedSpecificTime = useMemo(() => {
    const period = specificHour >= 12 ? "pm" : "am";
    const displayHour = specificHour % 12 || 12;
    const displayMinute = String(specificMinute).padStart(2, "0");
    return `${String(displayHour)}:${displayMinute}${period}`;
  }, [specificHour, specificMinute]);

  return (
    <View>
      <Text textStyle="lg/medium" mb="lg">
        {isAdjustMode ? adjustTitle : armDisarmedTitle}
      </Text>
      <Text textStyle="sm/regular" color="textMuted">
        {isAdjustMode ? adjustSubtitle : armDisarmedSubtitle}
      </Text>

      <Animated.View layout={LinearTransition} style={styles.contentContainer}>
        {/* Option 1: For a set duration */}
        <Animated.View layout={LinearTransition}>
          <View style={styles.optionContainer}>
            <Pressable onPress={() => setSelectionMode("duration")}>
              <View style={styles.optionRow}>
                <RadioButton selected={selectionMode === "duration"} onSelect={() => setSelectionMode("duration")} />
                <Text textStyle="sm/medium">For a set duration:</Text>
              </View>
            </Pressable>
            {selectionMode === "duration" && (
              <View style={styles.durationContentContainer}>
                <SegmentedControl
                  options={TIME_UNIT_OPTIONS}
                  selectedOption={timeUnit}
                  onSelectOption={handleTimeUnitPress}
                  {...(selectorWidth != null ? { width: selectorWidth } : {})}
                  containerStyle={{
                    borderColor: theme.colors.field.border_default,
                  }}
                  activeBoxStyle={{ backgroundColor: theme.colors.surface1.hover }}
                  textColorActiveBox="white"
                />
                <IncrementableInput
                  value={timeValue}
                  onChange={setTimeValue}
                  min={1}
                  max={timeMax}
                  error={hasTimeError}
                  helperText={timeHelperText}
                />
              </View>
            )}
          </View>
        </Animated.View>

        {/* Option 2: Until a specific time */}
        <Animated.View layout={LinearTransition}>
          <View style={styles.optionContainer}>
            <Pressable onPress={() => setSelectionMode("specificTime")}>
              <View style={styles.optionRow}>
                <RadioButton
                  selected={selectionMode === "specificTime"}
                  onSelect={() => setSelectionMode("specificTime")}
                />
                <Text textStyle="sm/medium">Until a specific time:</Text>
              </View>
            </Pressable>
            {selectionMode === "specificTime" && (
              <View style={styles.timePickerWrapper}>
                <Pressable onPress={() => setShowTimePicker(true)}>
                  <View style={styles.specificTimeContentContainer}>
                    <Text textStyle="md/regular">{formattedSpecificTime}</Text>
                    <Icon name="clockOutline" size={20} />
                  </View>
                </Pressable>
                {renderTimePicker?.({
                  isVisible: showTimePicker,
                  hour: specificHour,
                  minute: specificMinute,
                  onConfirm: (hour, minute) => {
                    setSpecificHour(hour);
                    setSpecificMinute(minute);
                    setShowTimePicker(false);
                  },
                  onCancel: () => setShowTimePicker(false),
                })}
              </View>
            )}
          </View>
        </Animated.View>

        {/* Option 3: Indefinitely */}
        <Animated.View layout={LinearTransition}>
          <Pressable onPress={() => setSelectionMode("indefinitely")}>
            <View style={styles.optionRow}>
              <RadioButton
                selected={selectionMode === "indefinitely"}
                onSelect={() => setSelectionMode("indefinitely")}
              />
              <Text textStyle="sm/medium">Indefinitely</Text>
            </View>
          </Pressable>
        </Animated.View>
      </Animated.View>

      {bannerMessage && (
        <Animated.View layout={LinearTransition} style={styles.bannerContainer}>
          <Banner title="Schedule Override" message={bannerMessage} />
        </Animated.View>
      )}

      <Animated.View layout={LinearTransition} style={styles.buttonContainer}>
        <Button
          label="Confirm"
          onPress={handleConfirm}
          variant="primary"
          style={{ height: 40 }}
          loading={isSubmitting}
          disabled={isSubmitting || hasTimeError}
        />
      </Animated.View>
    </View>
  );
});

const styles = StyleSheet.create((theme) => ({
  contentContainer: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    padding: theme.spacing["3xl"],
    marginTop: theme.spacing["3xl"],
  },
  optionContainer: {
    marginBottom: theme.spacing["10xl"],
  },
  durationContentContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
    marginTop: theme.spacing.lg,
    marginLeft: theme.spacing["4xl"],
  },
  timePickerWrapper: {
    marginTop: theme.spacing.lg,
    marginLeft: theme.spacing["4xl"],
  },
  specificTimeContentContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.interactive.buttons.default_background,
    paddingVertical: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
  },
  bannerContainer: {
    marginTop: theme.spacing["5xl"],
  },
  buttonContainer: {
    marginTop: theme.spacing["5xl"],
    marginBottom: theme.spacing["3xl"],
  },
  optionRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.lg,
  },
}));
